import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Save, ExternalLink, Globe, FileText, Layers, Handshake, Building2, Info, Scale } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { OgImageUploader } from "@/components/marketing/OgImageUploader";
import PartnerEditor from "@/pages/app/PartnerEditor";
import EnterpriseEditor from "@/pages/app/EnterpriseEditor";
import AboutEditor from "@/pages/app/AboutEditor";
import LegalEditor from "@/pages/app/LegalEditor";

const ROBOTS_OPTIONS = [
  { value: "index, follow", label: "Index, Follow (default)" },
  { value: "noindex, follow", label: "No-index, Follow" },
  { value: "index, nofollow", label: "Index, No-follow" },
  { value: "noindex, nofollow", label: "No-index, No-follow" },
];
const TWITTER_OPTIONS = [
  { value: "summary_large_image", label: "Summary, large image" },
  { value: "summary", label: "Summary" },
];

function Counter({ value, max }) {
  const len = (value || "").length;
  const over = len > max;
  return <span className={`text-xs tabular-nums ${over ? "font-[600] text-destructive" : "text-muted-foreground"}`}>{len}/{max}</span>;
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between"><Label className="text-sm">{label}</Label></div>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function PageEditor({ page, onChange }) {
  const set = (k, v) => onChange({ ...page, [k]: v });
  const k = page.page_key;
  return (
    <div className="space-y-5 pt-1" data-testid={`seo-page-editor-${k}`}>
      <Field label={<span className="flex items-center gap-2">SEO title <Counter value={page.title} max={60} /></span>}>
        <Input value={page.title || ""} onChange={(e) => set("title", e.target.value)} data-testid={`seo-${k}-title`} placeholder="Page title for search results" />
      </Field>
      <Field label={<span className="flex items-center gap-2">Meta description <Counter value={page.meta_description} max={160} /></span>}>
        <Textarea rows={2} value={page.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid={`seo-${k}-description`} placeholder="155–160 character summary shown under the title in Google" />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Keywords" hint="Comma-separated">
          <Input value={page.keywords || ""} onChange={(e) => set("keywords", e.target.value)} data-testid={`seo-${k}-keywords`} />
        </Field>
        <Field label="Canonical path" hint="Leave blank to use the page path">
          <Input value={page.canonical_path || ""} onChange={(e) => set("canonical_path", e.target.value)} data-testid={`seo-${k}-canonical`} placeholder={page.path} />
        </Field>
      </div>

      <div className="rounded-lg border border-border bg-secondary/30 p-4">
        <h4 className="text-sm font-[640]">Social sharing (Open Graph)</h4>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-4">
            <Field label="OG title" hint="Defaults to the SEO title">
              <Input value={page.og_title || ""} onChange={(e) => set("og_title", e.target.value)} data-testid={`seo-${k}-og-title`} />
            </Field>
            <Field label="OG description" hint="Defaults to the meta description">
              <Textarea rows={2} value={page.og_description || ""} onChange={(e) => set("og_description", e.target.value)} data-testid={`seo-${k}-og-description`} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="OG type"><Input value={page.og_type || "website"} onChange={(e) => set("og_type", e.target.value)} data-testid={`seo-${k}-og-type`} /></Field>
              <Field label="Twitter card">
                <Select value={page.twitter_card || "summary_large_image"} onValueChange={(v) => set("twitter_card", v)}>
                  <SelectTrigger data-testid={`seo-${k}-twitter-card`}><SelectValue /></SelectTrigger>
                  <SelectContent>{TWITTER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <OgImageUploader value={page.og_image_url} onChange={(u) => set("og_image_url", u)} testId={`seo-${k}-og-image`} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Robots">
          <Select value={page.robots || "index, follow"} onValueChange={(v) => set("robots", v)}>
            <SelectTrigger data-testid={`seo-${k}-robots`}><SelectValue /></SelectTrigger>
            <SelectContent>{ROBOTS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="flex items-end gap-3">
          <Switch checked={page.active !== false} onCheckedChange={(v) => set("active", v)} data-testid={`seo-${k}-active`} />
          <span className="pb-1.5 text-sm text-muted-foreground">Include in sitemap</span>
        </div>
      </div>

      <Field label="Schema markup (JSON-LD)" hint="Raw JSON-LD injected as a <script> tag. Must be valid JSON. Leave blank for none.">
        <Textarea rows={6} value={page.json_ld || ""} onChange={(e) => set("json_ld", e.target.value)} data-testid={`seo-${k}-jsonld`} className="font-mono text-xs" placeholder='{"@context":"https://schema.org","@type":"WebPage",...}' />
      </Field>
    </div>
  );
}

function TemplateEditor({ tmpl, onChange }) {
  const set = (k, v) => onChange({ ...tmpl, [k]: v });
  const e = tmpl.entity_type;
  return (
    <Card className="p-5" data-testid={`seo-template-${e}`}>
      <h3 className="text-base font-[640]">{tmpl.label || e}</h3>
      <p className="mt-1 text-xs text-muted-foreground">Placeholders: <code>{"{name}"}</code> <code>{"{tagline}"}</code> <code>{"{tagline_suffix}"}</code> <code>{"{description}"}</code> <code>{"{site_name}"}</code></p>
      <div className="mt-4 space-y-4">
        <Field label="Title template"><Input value={tmpl.title_template || ""} onChange={(ev) => set("title_template", ev.target.value)} data-testid={`seo-tmpl-${e}-title`} /></Field>
        <Field label="Description template"><Input value={tmpl.description_template || ""} onChange={(ev) => set("description_template", ev.target.value)} data-testid={`seo-tmpl-${e}-description`} /></Field>
        <Field label="Fallback description" hint="Used when the entity has no description"><Input value={tmpl.fallback_description || ""} onChange={(ev) => set("fallback_description", ev.target.value)} data-testid={`seo-tmpl-${e}-fallback`} /></Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="OG type"><Input value={tmpl.og_type || ""} onChange={(ev) => set("og_type", ev.target.value)} data-testid={`seo-tmpl-${e}-ogtype`} /></Field>
          <Field label="Schema @type" hint="e.g. Person, Organization, Event, Article"><Input value={tmpl.json_ld_type || ""} onChange={(ev) => set("json_ld_type", ev.target.value)} data-testid={`seo-tmpl-${e}-jsonldtype`} /></Field>
          <Field label="Robots">
            <Select value={tmpl.robots || "index, follow"} onValueChange={(v) => set("robots", v)}>
              <SelectTrigger data-testid={`seo-tmpl-${e}-robots`}><SelectValue /></SelectTrigger>
              <SelectContent>{ROBOTS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="max-w-sm">
          <OgImageUploader value={tmpl.default_og_image_url} onChange={(u) => set("default_og_image_url", u)} label="Default OG image (fallback)" testId={`seo-tmpl-${e}-ogimage`} />
        </div>
      </div>
    </Card>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [globalCfg, setGlobalCfg] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seo");
      setPages(data.pages || []);
      setTemplates(data.templates || []);
      setGlobalCfg(data.global || {});
    } catch {
      toast.error("Could not load SEO settings.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const updatePage = (i, p) => setPages((arr) => arr.map((x, idx) => (idx === i ? p : x)));
  const updateTemplate = (i, t) => setTemplates((arr) => arr.map((x, idx) => (idx === i ? t : x)));
  const setG = (k, v) => setGlobalCfg((g) => ({ ...g, [k]: v }));

  const validateJson = (str, where) => {
    if (str && str.trim()) {
      try { JSON.parse(str); } catch (e) { toast.error(`Invalid JSON-LD for ${where}: ${e.message}`); return false; }
    }
    return true;
  };

  const save = async () => {
    for (const p of pages) if (!validateJson(p.json_ld, p.label || p.page_key)) return;
    if (!validateJson(globalCfg.organization_json_ld, "Global organization schema")) return;
    setSaving(true);
    try {
      const payload = {
        pages: pages.map((p) => ({
          page_key: p.page_key, title: p.title, meta_description: p.meta_description,
          keywords: p.keywords, canonical_path: p.canonical_path, og_title: p.og_title,
          og_description: p.og_description, og_image_url: p.og_image_url, og_type: p.og_type,
          twitter_card: p.twitter_card, robots: p.robots, json_ld: p.json_ld, active: p.active !== false,
        })),
        templates: templates.map((t) => ({
          entity_type: t.entity_type, title_template: t.title_template,
          description_template: t.description_template, fallback_description: t.fallback_description,
          og_type: t.og_type, json_ld_type: t.json_ld_type,
          default_og_image_url: t.default_og_image_url, robots: t.robots,
        })),
        global_settings: {
          site_name: globalCfg.site_name, base_url: globalCfg.base_url,
          default_og_image_url: globalCfg.default_og_image_url, twitter_site: globalCfg.twitter_site,
          default_robots: globalCfg.default_robots, organization_json_ld: globalCfg.organization_json_ld,
        },
      };
      const { data } = await api.put("/admin/seo", payload);
      setPages(data.pages || []);
      setTemplates(data.templates || []);
      setGlobalCfg(data.global || {});
      toast.success("SEO settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save SEO settings.");
    } finally { setSaving(false); }
  };

  if (loading) return <Loading />;

  return (
    <div className="mx-auto max-w-5xl pb-24" data-testid="marketing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-[680] tracking-[-0.01em] text-foreground"><Megaphone className="h-6 w-6 text-primary" /> Marketing &amp; SEO</h1>
          <p className="mt-1 text-muted-foreground">Manage metadata, social cards, and schema for every public page.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer"><Button variant="secondary" data-testid="marketing-view-sitemap"><ExternalLink className="mr-2 h-4 w-4" /> Sitemap</Button></a>
          <Button onClick={save} disabled={saving} data-testid="marketing-save-top"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </div>

      <Tabs defaultValue="pages" className="mt-6">
        <TabsList>
          <TabsTrigger value="pages" data-testid="marketing-tab-pages"><FileText className="mr-2 h-4 w-4" /> Pages</TabsTrigger>
          <TabsTrigger value="dynamic" data-testid="marketing-tab-dynamic"><Layers className="mr-2 h-4 w-4" /> Dynamic pages</TabsTrigger>
          <TabsTrigger value="global" data-testid="marketing-tab-global"><Globe className="mr-2 h-4 w-4" /> Global</TabsTrigger>
          <TabsTrigger value="partner" data-testid="marketing-tab-partner"><Handshake className="mr-2 h-4 w-4" /> Partner page</TabsTrigger>
          <TabsTrigger value="enterprise" data-testid="marketing-tab-enterprise"><Building2 className="mr-2 h-4 w-4" /> Enterprise page</TabsTrigger>
          <TabsTrigger value="about" data-testid="marketing-tab-about"><Info className="mr-2 h-4 w-4" /> About page</TabsTrigger>
          <TabsTrigger value="legal" data-testid="marketing-tab-legal"><Scale className="mr-2 h-4 w-4" /> Legal pages</TabsTrigger>
        </TabsList>

        <TabsContent value="pages" className="mt-4">
          <Card className="p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full" data-testid="seo-pages-accordion">
              {pages.map((p, i) => (
                <AccordionItem key={p.page_key} value={p.page_key}>
                  <AccordionTrigger data-testid={`seo-page-trigger-${p.page_key}`}>
                    <div className="flex flex-1 items-center justify-between pr-3">
                      <span className="font-[600]">{p.label}</span>
                      <code className="text-xs text-muted-foreground">{p.path}</code>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <PageEditor page={p} onChange={(np) => updatePage(i, np)} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </TabsContent>

        <TabsContent value="dynamic" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">Detail pages (profiles, businesses, capital, events, resources) generate SEO automatically from each entity using these templates.</p>
          {templates.map((t, i) => (
            <TemplateEditor key={t.entity_type} tmpl={t} onChange={(nt) => updateTemplate(i, nt)} />
          ))}
        </TabsContent>

        <TabsContent value="global" className="mt-4">
          <Card className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Site name"><Input value={globalCfg.site_name || ""} onChange={(e) => setG("site_name", e.target.value)} data-testid="seo-global-site-name" /></Field>
              <Field label="Base URL" hint="Used for canonical + absolute OG image URLs"><Input value={globalCfg.base_url || ""} onChange={(e) => setG("base_url", e.target.value)} data-testid="seo-global-base-url" placeholder="https://startupotter.com" /></Field>
              <Field label="Twitter / X handle"><Input value={globalCfg.twitter_site || ""} onChange={(e) => setG("twitter_site", e.target.value)} data-testid="seo-global-twitter" placeholder="@startupotter" /></Field>
              <Field label="Default robots">
                <Select value={globalCfg.default_robots || "index, follow"} onValueChange={(v) => setG("default_robots", v)}>
                  <SelectTrigger data-testid="seo-global-robots"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROBOTS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="max-w-sm">
              <OgImageUploader value={globalCfg.default_og_image_url} onChange={(u) => setG("default_og_image_url", u)} label="Site-wide default OG image (1200×630)" testId="seo-global-og-image" />
            </div>
            <Field label="Organization schema (JSON-LD)" hint="Site-wide Organization markup. Must be valid JSON.">
              <Textarea rows={8} value={globalCfg.organization_json_ld || ""} onChange={(e) => setG("organization_json_ld", e.target.value)} data-testid="seo-global-org-jsonld" className="font-mono text-xs" />
            </Field>
          </Card>
        </TabsContent>

        <TabsContent value="partner" className="mt-4">
          <PartnerEditor />
        </TabsContent>

        <TabsContent value="enterprise" className="mt-4">
          <EnterpriseEditor />
        </TabsContent>

        <TabsContent value="about" className="mt-4">
          <AboutEditor />
        </TabsContent>

        <TabsContent value="legal" className="mt-4">
          <LegalEditor />
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button size="lg" onClick={save} disabled={saving} data-testid="marketing-save-bottom"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}
